import {datadogRum, type RumGlobal} from '@datadog/browser-rum';
import {once} from 'lodash';
import {useEffect} from 'react';

import {envIsPublicCloud} from '../../config';
import {getRouteMatch} from '../../routes/utils';
import {
  TTI_DOMAIN_INCLUSIONS,
  TTI_WANDB_ANALYTICS_DOMAIN,
} from '../profiler/constants';
import {
  hasViewEverBeenBackgrounded,
  resetViewBackgrounded,
} from '../viewVisibility';
import {
  ALLOWED_TRACING_URLS,
  DATADOG_CLIENT_TOKEN,
  DATADOG_RUM_APPLICATION_ID,
  DATADOG_SITE,
  DATADOG_UI_SERVICE,
} from './constants';
import {useDatadogContext} from './DatadogContext';
import {getDatadogRumViewTimings} from './datadogRumViewTimings';

const rumFacade = Object.create(datadogRum);

rumFacade.addDurationVital = skipFnIfHidden(datadogRum.addDurationVital);
rumFacade.startDurationVital = skipFnIfHidden(
  datadogRum.startDurationVital,
  ddRef
);
rumFacade.stopDurationVital = skipFnIfHidden(datadogRum.stopDurationVital);
rumFacade.addTiming = skipFnIfHidden(datadogRum.addTiming);
rumFacade.addAction = skipFnIfHidden(datadogRum.addAction);

/**
 * Returns a facade over the Datadog RUM global instance that automatically skips
 * certain tracking calls if the browser window has been backgrounded.
 * This prevents tracking metrics that may be inaccurate when the page is not visible.
 *
 * @returns The Datadog RUM facade with view visibility checks
 */
export function getDatadogRum(): RumGlobal {
  return rumFacade;
}

/**
 * React hook to access the Datadog RUM (Real User Monitoring) instance.
 *
 * @returns The Datadog RUM global instance
 */
export function useDatadogRum(): RumGlobal {
  return useDatadogContext().rum;
}

/**
 * Sets a global RUM context property. If the property changes or the component
 * calling this hook are unmounted, that context property is unset.
 *
 * @param key
 * @param value
 */
export function useRumGlobalContext(
  key: string,
  value: unknown,
  shouldLog: boolean = true
) {
  const datadogRum = useDatadogRum();

  useEffect(() => {
    if (shouldLog) {
      datadogRum.setGlobalContextProperty(key, value);
      return () => {
        datadogRum.removeGlobalContextProperty(key);
      };
    }
    return undefined;
  }, [datadogRum, key, value, shouldLog]);
}

/**
 * As with `useRumGlobalContext`, but does not unset when the component unmounts.
 * @param key
 * @param value
 * @param shouldLog
 */
export function usePersistentRumGlobalContext(
  key: string,
  value: unknown,
  shouldLog: boolean = true
) {
  const datadogRum = useDatadogRum();

  useEffect(() => {
    if (shouldLog) {
      datadogRum.setGlobalContextProperty(key, value);
    }
  }, [datadogRum, key, value, shouldLog]);
}

/**
 * Sets a property on the view context, which is retained until some other code
 * changes the current view.
 *
 * @param key
 * @param value
 * @param shouldLog
 */
export function usePersistentRumViewContext(
  key: string,
  value: unknown,
  shouldLog: boolean = true
) {
  const datadogRum = useDatadogRum();

  useEffect(() => {
    if (shouldLog) {
      datadogRum.setViewContextProperty(key, value);
    }
  }, [datadogRum, key, value, shouldLog]);
}

/**
 * Starts a new Datadog RUM view with the given name.
 * Clears view-specific timings and resets the view backgrounded state.
 * Sets the route name as a global context property.
 *
 * @param name - The name of the view/route being started
 */
export function startDatadogRumView(name?: string) {
  // See `addFirstTiming` -- clear out the list of already-recorded timings so
  // that they can be re-recorded for the new view.
  getDatadogRumViewTimings().clear();

  const datadogRum = getDatadogRum();
  resetViewBackgrounded();
  datadogRum.startView({name});

  // QUESTION - is this necessary/used? setting session-level properties to
  //            values that change frequently seems incorrect.
  datadogRum.setGlobalContextProperty('routeName', name);
}

/**
 * Initializes Datadog RUM (Real User Monitoring) with configuration for session replay,
 * user tracking, resource monitoring, and custom event filtering.
 * This function is wrapped with `once()` to ensure it only runs once.
 */
export const initDatadogRum = once(function initDatadogRum() {
  datadogRum.init({
    allowedTracingUrls: ALLOWED_TRACING_URLS as any,

    applicationId: DATADOG_RUM_APPLICATION_ID,
    beforeSend(event) {
      // https://docs.datadoghq.com/real_user_monitoring/guide/enrich-and-control-rum-data/?tab=event#discard-a-frontend-error
      const discardedErrorMessages = [
        "'measure' on 'Performance': The mark 'GTM", // Google Tag manager
        'Performance.measure: Given mark name', // Google Tag manager
        'https://jscloud.net/x/25796/inlinks.js',
      ];

      if (event.type === 'error') {
        if (
          discardedErrorMessages.some(msg => event.error.message.includes(msg))
        ) {
          return false;
        }
      }

      const context = event.context ?? {};
      event.context = context;

      const url = new URL(event.view.url);
      const matchParams = getRouteMatch(url.pathname)?.params as any;
      context.gitTag = window.CONFIG?.GIT_TAG;
      context.envIsPublicCloud = envIsPublicCloud;
      context.identity = {
        entity: matchParams?.entityName,
        project: matchParams?.projectName,
        org: window.viewer?.org,
      };
      context.cacheEnabled = true;

      return true;
    },
    clientToken: DATADOG_CLIENT_TOKEN,
    // SECURITY: Mask all text in session replays to prevent sensitive data exposure.
    // If you want to see an element in session replay, use data-dd-privacy="allow"
    // on specific elements to selectively unmask non-sensitive content.
    defaultPrivacyLevel: 'mask',
    env: window.CONFIG?.ENVIRONMENT_NAME,
    profilingSampleRate: 25,
    service: DATADOG_UI_SERVICE,
    version: window.CONFIG?.GIT_TAG,
    sessionReplaySampleRate: 100,
    site: DATADOG_SITE,
    traceSampleRate: 100,
    trackUserInteractions: true,
    trackLongTasks: true,
    trackResources: true,
    // Only include activity from domains that are in TTI_DOMAIN_INCLUSIONS
    excludedActivityUrls: [
      (url: string) => {
        const urlobj = new URL(url);
        return !TTI_DOMAIN_INCLUSIONS.some(
          domain =>
            urlobj.hostname.includes(domain) &&
            // exclude analytics.wandb.ai - this is a special case because it includes wandb.ai, which is allowed
            urlobj.hostname !== TTI_WANDB_ANALYTICS_DOMAIN
        );
      },
    ],
    trackViewsManually: true,
  });
});

/** Internal functions - please think twice before exporting --------------------------------- */

/**
 * Wraps a Datadog analytics function and returns a new function with the same
 * signature. If the current browser window has ever been backgrounded during
 * the current view, the Datadog function will not be called. This prevents
 * inaccurate metrics from being recorded when the page is not visible.
 * In either case the return value should be the same-ish.
 *
 * @param fn - The Datadog function to wrap
 * @param generateReturnOnHidden - Optional function to generate a return value when the view is hidden
 * @returns A wrapped function that checks view visibility before calling the original
 */
function skipFnIfHidden<Fn extends (...args: any) => any>(
  fn: Fn,
  generateReturnOnHidden?: () => ReturnType<Fn>
) {
  return function (this: any, ...args: Parameters<Fn>): ReturnType<Fn> {
    if (hasViewEverBeenBackgrounded()) {
      if (generateReturnOnHidden) {
        return generateReturnOnHidden();
      }
      return undefined as ReturnType<Fn>;
    }
    return fn.call(this, ...args);
  };
}

/**
 * Each call returns a new reference identifier. Datadog uses these references
 * to start/stop vital durations, and unique references prevent accidentally
 * ending the wrong duration measurement.
 *
 * @returns A unique duration vital reference object
 */
function ddRef(): ReturnType<RumGlobal['startDurationVital']> {
  return {
    __dd_vital_reference: true,
  };
}
